import { AfterViewInit, Component, EventEmitter, inject, Input, NgZone, OnDestroy, Output, signal } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { faMicrophone, faCircleStop } from '@fortawesome/pro-duotone-svg-icons';
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { faPaperPlaneTop, faPause, faPlay } from '@fortawesome/pro-solid-svg-icons';

import { interval, Subject, takeUntil } from 'rxjs';
import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record';

import { WaveConfigOptions } from '../../models/waveconfig.model';
import { WaveformService } from '../../services/waveform/waveform.service';

@Component({
  selector: 'empathy-audio-recorder',
  templateUrl: './audio-recorder.component.html',
  styleUrls: ['./audio-recorder.component.scss']
})
export class AudioRecorderComponent implements AfterViewInit, OnDestroy {
  @Input() maxDuration = 90;
  @Input({ required: true }) settings: WaveConfigOptions;

  @Output() abortRecordingEvent = new EventEmitter<{ audioDenied: boolean }>();
  @Output() completeRecordingEvent: EventEmitter<Blob> = new EventEmitter<Blob>();
  @Output() processing = new EventEmitter<boolean>();

  faMicrophone = faMicrophone;
  faCircleStop = faCircleStop;
  faTrashCan = faTrashCan;
  faPaperPlaneTop = faPaperPlaneTop;
  faPause = faPause;
  faPlay = faPlay;

  dataAvailable$ = signal(false);
  disabled$ = signal(false);
  paused$ = signal(false);
  pausedPlayback$ = signal(true);
  progressTime$ = signal('0:00');
  recordingTime$ = signal(0);
  stopped$ = signal(false);
  processing$ = signal(false);

  private recordedData = new Uint8Array();
  private timerObservable: Subject<void>;

  private record: RecordPlugin;
  private waveSurferRecorder: WaveSurfer;
  private waveSurferRecord: WaveSurfer;

  private snackbar = inject(MatSnackBar);

  constructor(
    private waveformService: WaveformService,
    private ngZone: NgZone
  ) {}

  ngAfterViewInit(): void {
    this.createWaveSurferRecorder();

    RecordPlugin.getAvailableAudioDevices().then((devices: MediaDeviceInfo[]) => {
      const deviceId = devices[0].deviceId;

      this.record
        .startRecording({ deviceId })
        .then(() => this.startTimer())
        .catch(() => {
          this.snackbar.open('There was an error opening the microphone. Check the browser settings', null, { duration: 3000, panelClass: 'alert' });
          this.abortRecording(true);
        });
    });
  }

  ngOnDestroy(): void {
    this.destroyWaveSurfer();
  }

  abortRecording(audioDenied: boolean = false): void {
    this.dataAvailable$.set(false);
    this.disabled$.set(false);

    this.destroyWaveSurfer();

    this.abortRecordingEvent.emit({ audioDenied });
  }

  pauseRecording(): void {
    if (!this.record.isPaused()) {
      this.record.pauseRecording();
      this.pauseTimer();
      this.paused$.set(true);
    }
  }

  resumeRecording(): void {
    this.record.resumeRecording();
    this.startTimer();
    this.paused$.set(false);
    this.waveSurferRecord.pause();
    this.waveSurferRecord.setTime(0);
    this.pausedPlayback$.set(true);
  }

  playPauseRecordedAudio(): void {
    this.waveSurferRecord.playPause();
    this.pausedPlayback$.update((state) => !state);
  }

  sendAudioMessage(): void {
    this.dataAvailable$.set(true);
    this.disabled$.set(false);
    this.stopRecording();
    this.processing$.set(true);
    this.processing.emit(true);
  }

  private createWaveSurferRecorder(): void {
    if (this.waveSurferRecorder) {
      this.waveSurferRecorder.destroy();
    }

    this.waveSurferRecorder = this.waveformService.initWaveform({
      container: '#recorder',
      autoScroll: this.settings.autoScroll,
      autoCenter: this.settings.autoCenter,
      dragToSeek: this.settings.dragToSeek,
      fillParent: this.settings.fillParent,
      normalize: this.settings.normalize,
      waveColor: this.settings.waveColor,
      progressColor: this.settings.progressColor,
      cursorColor: this.settings.cursorColor,
      height: this.settings.height,
      cursorWidth: this.settings.cursorWidth,
      barWidth: this.settings.barWidth,
      barGap: this.settings.barGap,
      barRadius: this.settings.barRadius
    });

    this.record = this.waveSurferRecorder.registerPlugin(
      RecordPlugin.create({
        renderRecordedAudio: false,
        mediaRecorderTimeslice: 100
      })
    );

    this.record.on('record-end', (blob: Blob) => {
      if (this.dataAvailable$()) {
        this.ngZone.run(() => {
          this.destroyWaveSurfer();
          this.completeRecordingEvent.emit(blob);
        });
      }
    });

    this.record.on('record-data-available', (blob: Blob) => {
      if (!this.dataAvailable$()) {
        this.ngZone.run(() => {
          blob.arrayBuffer().then((data) => {
            const tmp = new Uint8Array(this.recordedData.byteLength + data.byteLength);
            tmp.set(new Uint8Array(this.recordedData), 0);
            tmp.set(new Uint8Array(data), this.recordedData.byteLength);
            this.recordedData = tmp;
            const recordedBlob = new Blob([tmp], { type: blob.type });

            if (this.paused$()) {
              this.createWaveSurferRecord(recordedBlob);
            }
          });
        });
      }
    });
  }

  private createWaveSurferRecord(blob: Blob): void {
    const url = URL.createObjectURL(blob);

    if (this.waveSurferRecord) {
      this.waveSurferRecord.destroy();
    }

    this.waveSurferRecord = this.waveformService.initWaveform({
      container: '#record',
      autoScroll: this.settings.autoScroll,
      autoCenter: this.settings.autoCenter,
      dragToSeek: this.settings.dragToSeek,
      fillParent: this.settings.fillParent,
      normalize: this.settings.normalize,
      waveColor: this.settings.waveColor,
      progressColor: this.settings.progressColor,
      cursorColor: this.settings.cursorColor,
      height: this.settings.height,
      cursorWidth: this.settings.cursorWidth,
      barWidth: this.settings.barWidth,
      barGap: this.settings.barGap,
      barRadius: this.settings.barRadius,
      url
    });

    this.waveSurferRecord.load(url);

    this.waveSurferRecord.on('finish', () => {
      this.pausedPlayback$.set(true);
    });
  }

  private destroyWaveSurfer(): void {
    if (this.record && this.record.isRecording()) {
      this.stopRecording();
    }

    if (this.record) {
      this.record.stopMic();
      this.record.unAll();
      this.record.destroy();
    }
    if (this.waveSurferRecord) {
      this.waveSurferRecord.unAll();
      this.waveSurferRecord.destroy();
    }
    if (this.waveSurferRecorder) {
      this.waveSurferRecorder.unAll();
      this.waveSurferRecorder.destroy();
    }
    this.record = null;
    this.waveSurferRecord = null;
    this.waveSurferRecorder = null;
  }

  private pauseTimer(): void {
    this.timerObservable.next();
    this.timerObservable.complete();
  }

  private updateProgress(): void {
    const time = this.recordingTime$();
    if (time >= this.maxDuration) {
      this.stopRecording();
    }

    const min = Math.floor((time / 60) << 0);
    const sec = Math.floor(time % 60);
    this.progressTime$.set(`${min}:${sec < 10 ? String(sec).padStart(2, '0') : sec}`);
  }

  private startTimer(): void {
    this.timerObservable = new Subject<void>();
    interval(1000)
      .pipe(takeUntil(this.timerObservable))
      .subscribe(() => {
        this.recordingTime$.update((time) => time + 1);
        this.updateProgress();
      });
  }

  private stopRecording(): void {
    this.record.stopRecording();
    this.paused$.set(true);
    this.stopped$.set(true);
    this.pauseTimer();
  }
}
